import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, X } from 'lucide-react';
import { api } from './api';
import { RichEditor } from '../components/RichEditor';

const CATEGORIES = ['Fleisch', 'Fisch', 'Gemüse', 'Snacks', 'Besondere Anlässe', 'Glutenfreie Rezepte'];
const DIFFICULTY = ['Einfach', 'Mittel', 'Anspruchsvoll'];

const emptyRecipe = {
  title: '', slug: '', status: 'draft', kurzbeschreibung: '', einleitung: '',
  hauptbild: null, weitere_bilder: [], zutaten: [], zubereitung: [],
  zubereitungszeit: '', gesamtzeit: '', portionen: '', hundegroesse: '', hundegewicht: '',
  kategorie: 'Fleisch', schwierigkeit: 'Einfach', glutenfrei: false,
  allergene: [], aufbewahrung: '', hinweise: [], tipps: '',
  autor: 'Felix Genuss Redaktion', veroeffentlicht_am: '',
  seo: { seo_title: '', meta_description: '', canonical_url: '', og_title: '', og_description: '', og_image: '' },
};

const slugify = (t) => t.toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

function Field({ id, label, children, hint }) {
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-sm font-semibold">{label}</label>
      {children}
      {hint && <p className="mt-1 text-xs text-brand-brown-soft">{hint}</p>}
    </div>
  );
}

const inputCls = 'min-h-11 w-full rounded-sm border border-brand-brown/25 bg-white px-3';

function ImagePicker({ label, value, onChange, testid }) {
  const [media, setMedia] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    if (media) { setOpen((v) => !v); return; }
    api('/admin/media').then((d) => { setMedia(d.media); setOpen(true); }).catch(() => setMedia([]));
  };

  return (
    <div className="rounded-sm border border-brand-line bg-white p-4">
      <p className="text-sm font-semibold">{label}</p>
      {value?.url ? (
        <div className="mt-2 flex items-start gap-3">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${value.url}`} alt={value.alt || ''} className="h-20 w-20 rounded-sm border border-brand-line object-cover" />
          <div className="min-w-0 flex-1">
            <label htmlFor={`${testid}-alt`} className="text-xs font-semibold">Alt-Text</label>
            <input id={`${testid}-alt`} value={value.alt} onChange={(e) => onChange({ ...value, alt: e.target.value })} className="mt-1 min-h-10 w-full rounded-sm border border-brand-brown/25 px-2 text-sm" data-testid={`${testid}-alt`} />
            <button type="button" onClick={() => onChange(null)} className="mt-2 text-xs font-semibold text-red-800 underline underline-offset-2">Bild entfernen</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={load} aria-expanded={open} className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid={`${testid}-choose`}>
          <Plus className="h-4 w-4" aria-hidden="true" /> Bild aus Medienbibliothek wählen
        </button>
      )}
      {open && media && (
        <ul className="mt-3 grid max-h-64 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4" aria-label="Verfügbare Bilder">
          {media.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onClick={() => { onChange({ url: m.url, alt: m.alt, caption: m.caption || '' }); setOpen(false); }}
                className="block w-full rounded-sm border border-transparent p-1 hover:border-brand-olive focus-visible:border-brand-olive"
                data-testid={`${testid}-pick-${m.token}`}
              >
                <img src={`${process.env.REACT_APP_BACKEND_URL}${m.variants['640']}`} alt={m.alt || m.original_name} className="aspect-square w-full rounded-sm object-cover" loading="lazy" />
                <span className="mt-1 block truncate text-xs text-brand-brown-soft">{m.original_name}</span>
              </button>
            </li>
          ))}
          {media.length === 0 && <li className="col-span-full text-sm text-brand-brown-soft">Noch keine Bilder – lade zuerst eines unter „Medien“ hoch.</li>}
        </ul>
      )}
    </div>
  );
}

export default function RecipeEditor() {
  const { id } = useParams();
  const isNew = !id;
  const [recipe, setRecipe] = useState(emptyRecipe);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isNew) return;
    api(`/admin/recipes/${id}`)
      .then((r) => setRecipe({ ...emptyRecipe, ...r, seo: { ...emptyRecipe.seo, ...(r.seo || {}) } }))
      .catch((e) => setMessage({ type: 'error', text: e.message }))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  const set = (key, value) => setRecipe((r) => ({ ...r, [key]: value }));
  const setSeo = (key, value) => setRecipe((r) => ({ ...r, seo: { ...r.seo, [key]: value } }));

  const save = async (nextStatus) => {
    setSaving(true);
    setMessage(null);
    const payload = { ...recipe, status: nextStatus || recipe.status };
    try {
      const saved = isNew
        ? await api('/admin/recipes', { method: 'POST', body: JSON.stringify(payload) })
        : await api(`/admin/recipes/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
      setMessage({ type: 'success', text: payload.status === 'published' ? 'Rezept veröffentlicht.' : 'Rezept gespeichert.' });
      if (isNew) navigate(`/admin/rezepte/${saved.id}`, { replace: true });
      else setRecipe((r) => ({ ...r, ...saved, seo: { ...emptyRecipe.seo, ...(saved.seo || {}) } }));
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p role="status" className="text-brand-brown-soft">Lade Rezept …</p>;

  return (
    <div data-testid="recipe-editor">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-bold">{isNew ? 'Neues Rezept' : `Rezept bearbeiten`}</h1>
        {!isNew && (
          <Link to={`/rezepte/${recipe.slug}?vorschau=admin`} target="_blank" rel="noopener" className="btn-secondary" data-testid="editor-preview-link">
            Vorschau öffnen
          </Link>
        )}
      </div>

      {message && (
        <p role={message.type === 'error' ? 'alert' : 'status'} className={`mt-4 rounded-sm border p-4 text-sm font-semibold ${message.type === 'error' ? 'border-red-700 bg-red-50 text-red-800' : 'border-brand-olive bg-white text-brand-olive-dark'}`} data-testid="editor-message">
          {message.text}
        </p>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section className="space-y-5 border border-brand-line bg-brand-cream p-5" aria-labelledby="editor-basics">
          <h2 id="editor-basics" className="font-serif text-xl font-bold">Grundlagen</h2>
          <Field id="r-title" label="Titel">
            <input id="r-title" value={recipe.title} onChange={(e) => { set('title', e.target.value); if (isNew) set('slug', slugify(e.target.value)); }} className={inputCls} data-testid="r-title" />
          </Field>
          <Field id="r-slug" label="Slug" hint="Nur Kleinbuchstaben, Zahlen und Bindestriche. Wird zur URL /rezepte/slug">
            <input id="r-slug" value={recipe.slug} onChange={(e) => set('slug', slugify(e.target.value))} className={inputCls} data-testid="r-slug" />
          </Field>
          <Field id="r-kurz" label="Kurzbeschreibung">
            <textarea id="r-kurz" value={recipe.kurzbeschreibung} onChange={(e) => set('kurzbeschreibung', e.target.value)} rows={2} className="w-full rounded-sm border border-brand-brown/25 bg-white px-3 py-2" data-testid="r-kurz" />
          </Field>
          <RichEditor id="r-einleitung" label="Einleitung" value={recipe.einleitung} onChange={(v) => set('einleitung', v)} rows={6} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="r-kategorie" label="Kategorie">
              <select id="r-kategorie" value={recipe.kategorie} onChange={(e) => set('kategorie', e.target.value)} className={inputCls} data-testid="r-kategorie">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field id="r-schwierigkeit" label="Schwierigkeitsgrad">
              <select id="r-schwierigkeit" value={recipe.schwierigkeit} onChange={(e) => set('schwierigkeit', e.target.value)} className={inputCls} data-testid="r-schwierigkeit">
                {DIFFICULTY.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field id="r-zzeit" label="Zubereitungszeit">
              <input id="r-zzeit" value={recipe.zubereitungszeit} onChange={(e) => set('zubereitungszeit', e.target.value)} placeholder="z. B. 10 Minuten" className={inputCls} data-testid="r-zzeit" />
            </Field>
            <Field id="r-gzeit" label="Gesamtzeit">
              <input id="r-gzeit" value={recipe.gesamtzeit} onChange={(e) => set('gesamtzeit', e.target.value)} placeholder="z. B. 15 Minuten" className={inputCls} data-testid="r-gzeit" />
            </Field>
            <Field id="r-portionen" label="Anzahl Portionen">
              <input id="r-portionen" value={recipe.portionen} onChange={(e) => set('portionen', e.target.value)} className={inputCls} data-testid="r-portionen" />
            </Field>
            <Field id="r-groesse" label="Empfohlene Hundegrösse">
              <input id="r-groesse" value={recipe.hundegroesse} onChange={(e) => set('hundegroesse', e.target.value)} placeholder="z. B. kleine Hunde" className={inputCls} data-testid="r-groesse" />
            </Field>
            <Field id="r-gewicht" label="Empfohlenes Hundegewicht">
              <input id="r-gewicht" value={recipe.hundegewicht} onChange={(e) => set('hundegewicht', e.target.value)} placeholder="z. B. bis 10 kg" className={inputCls} data-testid="r-gewicht" />
            </Field>
            <Field id="r-autor" label="Autor">
              <input id="r-autor" value={recipe.autor} onChange={(e) => set('autor', e.target.value)} className={inputCls} data-testid="r-autor" />
            </Field>
            <Field id="r-datum" label="Veröffentlichungsdatum">
              <input id="r-datum" type="date" value={(recipe.veroeffentlicht_am || '').slice(0, 10)} onChange={(e) => set('veroeffentlicht_am', e.target.value ? new Date(e.target.value).toISOString() : '')} className={inputCls} data-testid="r-datum" />
            </Field>
            <div className="flex items-end pb-1">
              <label className="flex min-h-11 items-center gap-2 text-sm font-semibold">
                <input type="checkbox" checked={recipe.glutenfrei} onChange={(e) => set('glutenfrei', e.target.checked)} className="h-5 w-5 accent-brand-olive" data-testid="r-glutenfrei" />
                Glutenfrei
              </label>
            </div>
          </div>
        </section>

        <section className="space-y-5 border border-brand-line bg-brand-cream p-5" aria-labelledby="editor-content">
          <h2 id="editor-content" className="font-serif text-xl font-bold">Inhalt</h2>

          <ImagePicker label="Hauptbild" value={recipe.hauptbild} onChange={(v) => set('hauptbild', v)} testid="r-hauptbild" />

          <div>
            <p className="text-sm font-semibold">Zutaten & Mengen</p>
            <ul className="mt-2 space-y-2">
              {recipe.zutaten.map((z, i) => (
                <li key={i} className="flex gap-2">
                  <input aria-label={`Menge Zeile ${i + 1}`} value={z.menge} onChange={(e) => set('zutaten', recipe.zutaten.map((x, j) => j === i ? { ...x, menge: e.target.value } : x))} placeholder="80 g" className="min-h-11 w-24 rounded-sm border border-brand-brown/25 bg-white px-2 text-sm" data-testid={`zutat-menge-${i}`} />
                  <input aria-label={`Zutat Zeile ${i + 1}`} value={z.zutat} onChange={(e) => set('zutaten', recipe.zutaten.map((x, j) => j === i ? { ...x, zutat: e.target.value } : x))} placeholder="Zutat" className="min-h-11 flex-1 rounded-sm border border-brand-brown/25 bg-white px-2 text-sm" data-testid={`zutat-name-${i}`} />
                  <button type="button" onClick={() => set('zutaten', recipe.zutaten.filter((_, j) => j !== i))} aria-label={`Zeile ${i + 1} entfernen`} className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-red-700 text-red-800 hover:bg-red-50">
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => set('zutaten', [...recipe.zutaten, { menge: '', zutat: '' }])} className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid="add-zutat">
              <Plus className="h-4 w-4" aria-hidden="true" /> Zutat hinzufügen
            </button>
          </div>

          <div>
            <p className="text-sm font-semibold">Zubereitung (Schritte)</p>
            <ol className="mt-2 space-y-2">
              {recipe.zubereitung.map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span aria-hidden="true" className="mt-2.5 w-6 shrink-0 text-center font-serif font-bold text-brand-olive">{i + 1}.</span>
                  <textarea aria-label={`Schritt ${i + 1}`} value={s} onChange={(e) => set('zubereitung', recipe.zubereitung.map((x, j) => j === i ? e.target.value : x))} rows={2} className="flex-1 rounded-sm border border-brand-brown/25 bg-white px-2 py-2 text-sm" data-testid={`schritt-${i}`} />
                  <button type="button" onClick={() => set('zubereitung', recipe.zubereitung.filter((_, j) => j !== i))} aria-label={`Schritt ${i + 1} entfernen`} className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-red-700 text-red-800 hover:bg-red-50">
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ol>
            <button type="button" onClick={() => set('zubereitung', [...recipe.zubereitung, ''])} className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid="add-schritt">
              <Plus className="h-4 w-4" aria-hidden="true" /> Schritt hinzufügen
            </button>
          </div>

          <Field id="r-allergene" label="Mögliche Allergene" hint="Kommagetrennt, z. B. Hähnchen, Ei">
            <input id="r-allergene" value={recipe.allergene.join(', ')} onChange={(e) => set('allergene', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} className={inputCls} data-testid="r-allergene" />
          </Field>
          <Field id="r-aufbewahrung" label="Aufbewahrung">
            <textarea id="r-aufbewahrung" value={recipe.aufbewahrung} onChange={(e) => set('aufbewahrung', e.target.value)} rows={2} className="w-full rounded-sm border border-brand-brown/25 bg-white px-3 py-2" data-testid="r-aufbewahrung" />
          </Field>
          <div>
            <p className="text-sm font-semibold">Hinweise</p>
            <ul className="mt-2 space-y-2">
              {recipe.hinweise.map((h, i) => (
                <li key={i} className="flex gap-2">
                  <input aria-label={`Hinweis ${i + 1}`} value={h} onChange={(e) => set('hinweise', recipe.hinweise.map((x, j) => j === i ? e.target.value : x))} className="min-h-11 flex-1 rounded-sm border border-brand-brown/25 bg-white px-2 text-sm" data-testid={`hinweis-${i}`} />
                  <button type="button" onClick={() => set('hinweise', recipe.hinweise.filter((_, j) => j !== i))} aria-label={`Hinweis ${i + 1} entfernen`} className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-sm border border-red-700 text-red-800 hover:bg-red-50">
                    <X className="h-4 w-4" aria-hidden="true" />
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" onClick={() => set('hinweise', [...recipe.hinweise, ''])} className="mt-2 inline-flex min-h-11 items-center gap-1.5 rounded-sm border border-brand-brown/30 px-3 py-2 text-sm font-semibold hover:bg-brand-beige" data-testid="add-hinweis">
              <Plus className="h-4 w-4" aria-hidden="true" /> Hinweis hinzufügen
            </button>
          </div>
          <RichEditor id="r-tipps" label="Zusätzliche Tipps" value={recipe.tipps} onChange={(v) => set('tipps', v)} rows={4} />
        </section>
      </div>

      <section className="mt-6 border border-brand-line bg-brand-cream p-5" aria-labelledby="editor-seo">
        <h2 id="editor-seo" className="font-serif text-xl font-bold">SEO</h2>
        <p className="mt-1 text-sm text-brand-brown-soft">Leere Felder werden automatisch sinnvoll aus Titel und Kurzbeschreibung ergänzt.</p>
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Field id="seo-title" label="SEO Title"><input id="seo-title" value={recipe.seo.seo_title} onChange={(e) => setSeo('seo_title', e.target.value)} className={inputCls} data-testid="seo-title" /></Field>
          <Field id="seo-desc" label="Meta Description"><input id="seo-desc" value={recipe.seo.meta_description} onChange={(e) => setSeo('meta_description', e.target.value)} className={inputCls} data-testid="seo-desc" /></Field>
          <Field id="seo-canonical" label="Canonical URL"><input id="seo-canonical" value={recipe.seo.canonical_url} onChange={(e) => setSeo('canonical_url', e.target.value)} className={inputCls} data-testid="seo-canonical" /></Field>
          <Field id="seo-og-title" label="Open Graph Title"><input id="seo-og-title" value={recipe.seo.og_title} onChange={(e) => setSeo('og_title', e.target.value)} className={inputCls} data-testid="seo-og-title" /></Field>
          <Field id="seo-og-desc" label="Open Graph Description"><input id="seo-og-desc" value={recipe.seo.og_description} onChange={(e) => setSeo('og_description', e.target.value)} className={inputCls} data-testid="seo-og-desc" /></Field>
          <Field id="seo-og-image" label="Open Graph Bild (Pfad)"><input id="seo-og-image" value={recipe.seo.og_image} onChange={(e) => setSeo('og_image', e.target.value)} className={inputCls} data-testid="seo-og-image" /></Field>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap gap-3">
        <button type="button" onClick={() => save()} disabled={saving} className="btn-secondary disabled:opacity-60" data-testid="save-draft-button">
          {saving ? 'Speichere …' : recipe.status === 'published' ? 'Änderungen speichern' : 'Als Entwurf speichern'}
        </button>
        {recipe.status !== 'published' && (
          <button type="button" onClick={() => save('published')} disabled={saving} className="btn-primary disabled:opacity-60" data-testid="publish-button">
            Veröffentlichen
          </button>
        )}
        {recipe.status === 'published' && (
          <button type="button" onClick={async () => { await api(`/admin/recipes/${id}/status`, { method: 'POST', body: JSON.stringify({ status: 'draft' }) }); set('status', 'draft'); setMessage({ type: 'success', text: 'Veröffentlichung zurückgezogen.' }); }} className="btn-secondary" data-testid="unpublish-button">
            Veröffentlichung zurückziehen
          </button>
        )}
      </div>
    </div>
  );
}
